import { For, Show } from "solid-js";
import { Avatar } from "@/components/ui/avatar";
import { msg } from "@lingui/core/macro";
import { MessagesSquare } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { getThreadsQuery, otherParticipant, queryKeys } from "@famidex/shared";

/**
 * 💬 Bandeja de chats (DM 1:1). Local-first [L], aislada por cuenta (stream
 * `chat_threads`): sólo aparecen hilos donde participo. Cada fila muestra el
 * OTRO participante (desnormalizado) y el último mensaje.
 */
export function ChatListPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const { userId } = session;

  const enabled = () => Boolean(drizzle() && userId());

  const threads = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.chat.threads(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getThreadsQuery(d, uid) : null;
    },
  });

  return (
    <div class="flex flex-col gap-6" data-testid="chat-list-page">
      <header>
        <h1 class="text-2xl font-semibold text-foreground">{t(msg`Mensajes`)}</h1>
        <p class="mt-1 text-sm text-muted-foreground">
          {t(msg`Conversaciones privadas con otros coleccionistas.`)}
        </p>
      </header>

      <Show
        when={(threads.data ?? []).length > 0}
        fallback={
          <div
            data-testid="chat-empty"
            class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-14 text-center text-muted-foreground"
          >
            <MessagesSquare class="h-8 w-8" />
            <p class="max-w-xs text-sm">
              {t(msg`Sin conversaciones todavía. Escribe a un coleccionista desde su perfil.`)}
            </p>
          </div>
        }
      >
        <ul class="flex flex-col gap-2">
          <For each={threads.data}>
            {(thread) => {
              const other = otherParticipant(thread, userId() ?? "");
              return (
                <Link
                  to="/chat/$threadId"
                  params={{ threadId: thread.id }}
                  data-testid="chat-thread-row"
                  class="flex items-center gap-3 rounded-xl border border-border/60 bg-card p-3 transition-colors hover:bg-accent/30"
                >
                  {/* `other.image` es una CLAVE de storage, no una URL: metida
                      a pelo en un `<img src>` el navegador la resolvería como
                      ruta relativa del front (404 silencioso, foto rota).
                      `<Avatar>` construye la URL con el origen de la API y cae
                      a las iniciales si la imagen falla. */}
                  <Avatar
                    userId={other.id}
                    name={other.name}
                    image={other.image}
                    size="lg"
                    class="h-11 w-11"
                    testId="chat-thread-avatar"
                  />
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-semibold text-foreground">
                      {other.name ?? t(msg`Coleccionista`)}
                    </p>
                    <p class="truncate text-xs text-muted-foreground">
                      {thread.last_message_preview ?? t(msg`Di hola`)}
                    </p>
                  </div>
                </Link>
              );
            }}
          </For>
        </ul>
      </Show>
    </div>
  );
}
